import { useEffect, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { Overview } from "../lib/api.ts";
import type { DrillDown } from "./leadList.tsx";
import { useDrillOpener, type Drill } from "./dealList.tsx";
import { chartTheme } from "../lib/chart.ts";
import { useThemeTick } from "./chrome.tsx";
import { ago, compact, money, num, pct, pctValue, shortDate } from "../lib/format.ts";
import { dbLabel, plural, t, type L } from "../i18n/lang.ts";
import { CHANNEL, UI } from "../i18n/dict.ts";
import { Th, useSortable, type SortColumn } from "./sortable.tsx";

echarts.use([BarChart, LineChart, PieChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

/**
 * Проза этих виджетов. Один блок в начале файла — см. App.tsx.
 *
 * Здесь только то, что виджет печатает САМ: заголовки своих колонок, подписи
 * серий графиков, пустые состояния. Заголовки карточек и подписи плиток
 * приходят пропсами уже переведёнными — экран знает, о чём его цифра, а
 * `Card` и `Kpi` не знают.
 */
const TX = {
  /* --- значок сравнения с предыдущим периодом */
  deltaNoChange: { ru: "без изменений против", en: "no change against" },
  deltaInPrevious: { ru: "в предыдущем периоде", en: "in the previous period" },
  deltaUpFrom: { ru: "выросло с", en: "up from" },
  deltaDownFrom: { ru: "упало с", en: "down from" },
  deltaSameLength: {
    ru: "в предыдущем периоде той же длины",
    en: "in the previous period of the same length",
  },
  /* Пункты, а не проценты: причина в комментарии у расчёта. Пробел внутри
     сокращения неразрывный — «п.» и «п.» не должны оказаться на разных
     строках, как и само число со значком. */
  points: { ru: "п. п.", en: "pp" },
  /* Пробел перед знаком процента неразрывный — то же правило, что в `pct()`. */
  underOnePct: { ru: "<1 %", en: "<1%" },

  /* --- воронка лидов */
  funnelCount: { ru: "Сколько", en: "Count" },
  noFunnel: { ru: "За этот период воронки нет.", en: "No funnel for this period." },
  historyStartsOn: { ru: "История стадий начинается с", en: "Stage history starts on" },
  historyOnlyFrom: { ru: "История стадий есть только с", en: "Stage history exists only from" },
  historyEarlierLeads: {
    ru: "Лиды, созданные раньше, показывают стадию, в которой оказались, а не путь, которым шли.",
    en: "Earlier leads show the stage they ended up in, not the path they took.",
  },
  outsideFunnel: { ru: "Сейчас стоят вне воронки:", en: "Currently sitting outside the funnel:" },

  /* --- источники лида */
  noLeads: { ru: "За этот период лидов нет.", en: "No leads in this period." },
  colQualified: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  colBecameDeal: { ru: "Стали сделкой", en: "Became a deal" },
  colCompletedEvents: { ru: "Завершённые праздники", en: "Completed events" },
  taggedByAnalytics: {
    ru: "с меткой от аналитики, а не из CRM",
    en: "tagged by analytics, not by the CRM",
  },
  /* Подсказка на плитке, у которой есть детализация. */
  drillOpenList: {
    ru: "Показать записи, из которых сложилось это число",
    en: "Show the records this number is made of",
  },

  /* Хвосты заголовков списка, который открывается по щелчку на числе. */
  drillAllLeads: { ru: "все лиды", en: "all leads" },
  drillQualified: { ru: "квалифицированные лиды", en: "qualified leads" },
  drillBecameDeal: { ru: "стали сделкой", en: "became a deal" },
  drillCompleted: { ru: "завершённые праздники", en: "completed events" },

  /* --- графики */
  seriesQualified: { ru: "Квалифицированные лиды", en: "Qualified leads" },
  mixTickets: { ru: "Билеты", en: "Tickets" },
  mixFood: { ru: "Еда и напитки", en: "Food & drinks" },
  /* Категории, а не чеки праздников: в кольце доли складываются в кассу. */
  mixBanquet: { ru: "Банкетные категории", en: "Banquet categories" },
  mixOther: { ru: "Прочее", en: "Other" },
  noPos: {
    ru: "По выбранным паркам и периоду данных кассы нет.",
    en: "No POS data for the selected parks and period.",
  },

  /* --- свежесть данных */
  oldestOf: { ru: "самый старый из", en: "oldest of" },
  newest: { ru: "самый свежий", en: "newest" },
  /* «из N источников» — родительный падеж после предлога. */
  sourcesOf: {
    one: { ru: "источника", en: "source" },
    few: { ru: "источников", en: "sources" },
    many: { ru: "источников", en: "sources" },
  },
  sourcesFailing: {
    one: { ru: "источник с ошибкой", en: "source failing" },
    few: { ru: "источника с ошибкой", en: "sources failing" },
    many: { ru: "источников с ошибкой", en: "sources failing" },
  },
  sourcesSyncing: {
    one: { ru: "источник синхронизируется", en: "source syncing" },
    few: { ru: "источника синхронизируются", en: "sources syncing" },
    many: { ru: "источников синхронизируются", en: "sources syncing" },
  },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

export function Card({
  title,
  hint,
  right,
  children,
  className = "",
  id,
}: {
  title?: string;
  hint?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Якорь для перехода по странице. Нужен там, где карточек много. */
  id?: string;
}): React.ReactElement {
  return (
    <div className={`card ${className}`} id={id}>
      {(title || right) && (
        <div className="card-head">
          {title && <h3>{title}</h3>}
          {hint && <span className="hint">{hint}</span>}
          <span className="spacer" />
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

/**
 * Изменение к предыдущему периоду.
 *
 * Показывается ПРОЦЕНТОМ, когда база достаточно велика, и АБСОЛЮТНОЙ разницей,
 * когда мала. Причина простая: «+100 %» при переходе с одного события на два
 * формально верно и совершенно бесполезно, а «+1» — понятно. Порог 20 выбран по
 * тому, что ниже него суточные величины этого бизнеса шумят сильнее, чем растут.
 *
 * Направление подписано словом в `title`, а не только цветом и стрелкой: у части
 * величин рост — это плохо (потерянные лиды, нераспределённый расход), и цвет
 * там пришлось бы инвертировать, а слово читается одинаково.
 */
const SMALL_BASE = 20;

export function Delta({
  value,
  prev,
  inverse = false,
  kind = "count",
}: {
  value: number;
  prev: number;
  /** true, если рост величины — плохая новость. */
  inverse?: boolean;
  /** Чем измерена величина: от этого зависит и текст значка, и подсказка. */
  kind?: "money" | "rate" | "count";
}): React.ReactElement | null {
  if (!Number.isFinite(value) || !Number.isFinite(prev)) return null;

  /**
   * База называется теми же единицами, что и само число. До 10.09.2026
   * подсказка печатала минорные единицы: у плитки «Completed revenue 134K AED»
   * стояло «up from 13 098 371» — число в сто раз больше показанного.
   */
  const base =
    kind === "money"
      ? `${money(prev, "AED", { compact: true, withCode: false })} AED`
      : kind === "rate"
        ? pctValue(prev)
        : num(prev);

  const diff = value - prev;
  if (diff === 0) {
    return (
      <span
        className="delta flat"
        title={`${t(TX.deltaNoChange)} ${base} ${t(TX.deltaInPrevious)}`}
      >
        =
      </span>
    );
  }

  const up = diff > 0;
  const good = inverse ? !up : up;

  /**
   * У доли разница считается В ПУНКТАХ. Охват атрибуции, выросший с 1,7 % до
   * 2,0 %, это «+0,3 пункта»; «+18 %» прочитали бы как охват восемнадцать
   * процентов, и это была бы уже не оговорка, а неверное число.
   */
  const text =
    kind === "rate"
      ? `${up ? "+" : "−"}${num(Math.abs(Math.round(diff * 10) / 10))} ${t(TX.points)}`
      : Math.abs(prev) < SMALL_BASE || prev === 0
        ? `${up ? "+" : "−"}${num(Math.abs(diff))}`
        : /*
           * Округлённый до целого процент бывает нулём при ненулевой разнице, и
           * «▲ +0 %» читается как «вырос на ничего». В этом случае честнее
           * сказать, что изменение меньше процента, чем печатать ноль рядом со
           * стрелкой.
           */
          Math.abs(Math.round((100 * diff) / prev)) === 0
          ? `${up ? "+" : "−"}${t(TX.underOnePct)}`
          : `${up ? "+" : "−"}${pctValue(Math.abs(Math.round((100 * diff) / prev)), 0)}`;

  return (
    <span
      className={`delta ${good ? "up" : "down"}`}
      title={`${up ? t(TX.deltaUpFrom) : t(TX.deltaDownFrom)} ${base} ${t(TX.deltaSameLength)}`}
    >
      {up ? "▲" : "▼"} {text}
    </span>
  );
}

/**
 * Кнопка вокруг цифры плитки. Инлайном, а не классом: `theme.css` не мой файл,
 * а подходящего класса в нём нет (`.btn-ghost` рисует пилюлю с рамкой — вокруг
 * крупной цифры это мусор). Нужный класс назван в отчёте.
 *
 * `font: inherit` обязателен: у элементов формы шрифт свой, и цифра потеряла бы
 * фирменный кегль из `.kpi .value`. Пунктирное подчёркивание — единственный
 * признак кликабельности, который НЕ сдвигает разметку: цифры на пяти экранах
 * стоят в ряд, и любая рамка или отступ развалили бы ряд.
 */
export const VALUE_BUTTON: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "baseline",
  margin: 0,
  padding: 0,
  border: 0,
  background: "none",
  color: "inherit",
  font: "inherit",
  textAlign: "inherit",
  cursor: "pointer",
  textDecoration: "underline",
  textDecorationStyle: "dotted",
  textUnderlineOffset: 4,
};

/**
 * Число, под которым открывается список.
 *
 * Живёт здесь, рядом с `Kpi`, а не на экране: числа с детализацией стоят на
 * четырёх экранах, и вторая копия этого компонента означала бы две разные
 * подсказки и два разных поведения у одного и того же жеста.
 *
 * Отдельным компонентом, а не пропсом в каждую таблицу: числа, требующие
 * детализации, стоят и в ячейках таблиц, и внутри подписей плиток, и в
 * примечаниях под карточками. Открывашка берётся из контекста — так же, как её
 * берёт `Kpi`.
 *
 * `enabled` выключает ссылку на нуле в ТАБЛИЦАХ, тем же правилом, что и
 * `ChannelTable`: в строке таблицы ссылка на нуле — приглашение открыть пустое.
 * У плиток правило обратное и оно живёт в `Kpi`: там пустая панель со словами
 * «сделок нет» подтверждает ноль.
 *
 * `stopPropagation` нужен внутри строк, у которых есть свой обработчик щелчка
 * (таблица парков внутри карточки бренда сужает фильтры дашборда), иначе один
 * щелчок делал бы два дела.
 */
export function DrillNum({
  children,
  drill,
  enabled = true,
}: {
  children: React.ReactNode;
  drill: Drill;
  enabled?: boolean;
}): React.ReactElement {
  const open = useDrillOpener();
  if (!enabled || open === null) return <>{children}</>;
  return (
    <a
      href="#"
      title={t(TX.drillOpenList)}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        open(drill);
      }}
    >
      {children}
    </a>
  );
}

export function Kpi({
  label,
  value,
  unit,
  foot,
  tone,
  delta,
  drill,
}: {
  label: string;
  value: string;
  unit?: string;
  foot?: React.ReactNode;
  tone?: "good" | "bad" | "flat";
  /** Сравнение с предыдущим периодом. Отсутствует, когда сравнение выключено. */
  delta?: { value: number; prev: number; inverse?: boolean; kind?: "money" | "rate" | "count" };
  /**
   * Детализация: список записей, из которых сложилась цифра. Одна строка на
   * плитку — `drill={{ kind: "deals", title: …, slice: { dateBasis: …, … } }}` —
   * и плитка становится кликабельной.
   *
   * Открывашка берётся из контекста (`DrillProvider` в `App.tsx`), а не
   * приходит вторым пропсом: плиток около сорока на пяти экранах, и проп
   * заставил бы каждый экран тянуть его через свою сигнатуру.
   *
   * Плитку с нулём это не выключает СПЕЦИАЛЬНО: пустая панель со словами
   * «сделок нет» подтверждает ноль, а мёртвая плитка оставляет человека
   * гадать, ноль это или сломалось.
   */
  drill?: Drill;
}): React.ReactElement {
  /* Хук зовётся всегда, до любых ветвлений: порядок вызова хуков не должен
     зависеть от того, есть у плитки детализация или нет. */
  const openDrill = useDrillOpener();

  const shown = (
    <span className="value" style={tone === "bad" ? { color: "var(--bad)" } : undefined}>
      {value}
      {unit && <span className="unit"> {unit}</span>}
    </span>
  );

  return (
    <div className="card kpi">
      <span className="label">{label}</span>
      <span style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
        {/* Без детализации разметка ровно та же, что была: цифра лежит в
            карточке сама, без обёрток. */}
        {drill !== undefined && openDrill !== null ? (
          <button
            type="button"
            style={VALUE_BUTTON}
            title={t(TX.drillOpenList)}
            onClick={() => openDrill(drill)}
          >
            {shown}
          </button>
        ) : (
          shown
        )}
        {delta && (
          <Delta
            value={delta.value}
            prev={delta.prev}
            inverse={delta.inverse}
            kind={delta.kind}
          />
        )}
      </span>
      {foot && <span className="foot">{foot}</span>}
    </div>
  );
}

/**
 * Lead funnel.
 *
 * Built from stage history: each row is how many leads of this cohort EVER
 * reached that stage. The first version counted the stage a lead sits in today,
 * which produced a plausible-looking chart that measured nothing — every lead
 * is in exactly one current stage, so the rows were a distribution and the
 * percentages between them meant nothing. On real data 514 leads reached
 * INTERESTED while only 153 are sitting there now.
 *
 * Percentages are share of the whole cohort, not step-to-step. The stages are
 * not strictly nested — a lead can go from INQUIRY straight to INTERESTED — so
 * a step-to-step number would imply a sequence that does not exist.
 */
export function LeadFunnel({
  funnel,
  stageNow,
  totalLeads,
  historyFrom,
  era,
}: {
  funnel: Overview["leads"]["funnel"];
  stageNow: Overview["leads"]["stageNow"];
  totalLeads: number;
  historyFrom: string | null;
  era: string;
}): React.ReactElement {
  const steps = [...funnel].sort((a, b) => a.funnel_order - b.funnel_order);
  const outside = stageNow.filter((s) => s.funnel_order === null).sort((a, b) => b.leads - a.leads);
  /*
   * Масштаб полосы — КОГОРТА ПЕРИОДА, а не самая крупная ступень.
   *
   * Проценты справа всегда считались от когорты, а длина полосы — от максимума
   * по ступеням. Пока первой ступени в воронке не было, разница не бросалась в
   * глаза; с возвращением INQUIRY она стала бы явной: полоса говорила бы «сто
   * процентов» там, где число рядом говорит, скажем, «двенадцать». Теперь длина
   * полосы И ЕСТЬ та доля, что написана справа.
   *
   * `steps` в максимуме остаётся страховкой: история стадий начинается позже
   * части лидов, и ступень изредка оказывается больше когорты — полоса тогда
   * упёрлась бы в край и молча соврала бы про «больше ста процентов».
   */
  const scale = Math.max(1, totalLeads, ...steps.map((s) => s.leads));

  if (steps.length === 0) {
    return (
      <div className="empty">
        {t(TX.noFunnel)}
        {historyFrom ? ` ${t(TX.historyStartsOn)} ${historyFrom}.` : ""}
      </div>
    );
  }

  return (
    <>
      {era !== "managed" && historyFrom && (
        <div className="note" style={{ marginBottom: 12 }}>
          {t(TX.historyOnlyFrom)} {historyFrom}. {t(TX.historyEarlierLeads)}
        </div>
      )}
      <div className="funnel">
        {/* Шапка колонок: заказчик читал «1 795 17 %» как одно число. */}
        <div className="funnel-row funnel-head">
          <span className="name" />
          <span />
          <span className="num">{t(TX.funnelCount)}</span>
          <span className="num">{t(UI.share)}</span>
        </div>
        {steps.map((s) => (
          <div className="funnel-row" key={s.stage_id}>
            <span className="name">{s.stage_name}</span>
            <div className="funnel-bar">
              <div className="funnel-fill" style={{ width: `${(100 * s.leads) / scale}%` }} />
            </div>
            <span className="num">{num(s.leads)}</span>
            <span className="num pct">{totalLeads ? pct(s.leads, totalLeads, 0) : "—"}</span>
          </div>
        ))}
      </div>
      {outside.length > 0 && (
        <div className="note" style={{ marginTop: 14 }}>
          {t(TX.outsideFunnel)}{" "}
          {outside.slice(0, 5).map((s, i) => (
            <span key={s.stage_id}>
              {i > 0 ? " · " : ""}
              {s.stage_name} {num(s.leads)}
            </span>
          ))}
        </div>
      )}
    </>
  );
}

type ChannelRow = Overview["leads"]["byChannel"][number];

/**
 * Колонки таблицы источников. Карта на уровне модуля — так предписано
 * механизмом для общей таблицы; заодно она не зависит от порядка определений
 * внутри компонента: подпись нужна доступу в тот же миг, когда идёт сортировка.
 */
const CHANNEL_COLS: Record<
  "source" | "leads" | "qualified" | "deal" | "completed",
  SortColumn<ChannelRow>
> = {
  /* Подпись — тем же выражением, что печатает ячейка (`label` ниже): порядок
     ключей базы и порядок переведённых слов — разные порядки. */
  source: { kind: "text", get: (r) => dbLabel(CHANNEL, r.channel_key, r.channel_name) },
  leads: { kind: "number", get: (r) => r.leads },
  qualified: { kind: "number", get: (r) => r.qualified },
  deal: { kind: "number", get: (r) => r.with_deal },
  completed: { kind: "number", get: (r) => r.with_won_deal },
};

/**
 * Источники лида, и каждая цифра раскрывается в список.
 *
 * Раньше таблица называлась «каналы» и брала канал из поля источника Битрикса, а
 * в этом поле НЕТ варианта Google: строка Google Ads показывала ноль при расходе
 * 5 000-5 800 AED в месяц. Теперь источник считается единым определением
 * (`core.v_lead_source`), тем же, что на экране маркетинга.
 *
 * Клик по любому числу открывает те лиды, из которых оно сложилось. Срез уезжает
 * на сервер теми же признаками, по которым посчитана ячейка, поэтому список и
 * ячейка не могут разойтись.
 */
export function ChannelTable({
  rows,
  onDrill,
}: {
  rows: Overview["leads"]["byChannel"];
  onDrill?: (d: DrillDown) => void;
}): React.ReactElement {
  /*
   * Сортируется ВСЯ выборка, а десятка отрезается после: `slice(0, 10)` здесь —
   * ограничение показа, а не страница, все источники уже на клиенте. Порядок
   * «отсортировать десятку лучших по лидам» дал бы лучших по сделкам ИЗ лучших
   * по лидам — то есть неверный максимум. Кружок и полоска красятся по позиции
   * строки (`tint(i)`), поэтому после сортировки фирменный ряд идёт сверху вниз
   * по новому порядку — так и задумано.
   *
   * Хук стоит ДО раннего выхода по пустой выборке: порядок вызова хуков
   * не должен зависеть от данных.
   */
  const S = useSortable(rows, CHANNEL_COLS);

  if (rows.length === 0) return <div className="empty">{t(TX.noLeads)}</div>;
  const max = Math.max(1, ...rows.map((r) => r.leads));

  /**
   * Цвет строки — переменной темы, а не значением: эти кружки и полоски рисует
   * браузер, поэтому фирменный ряд подставляется сам и в тёмной, и в светлой теме.
   */
  const tint = (i: number): string => `var(--s${(i % 8) + 1})`;

  /**
   * Подпись канала переводится ПО КЛЮЧУ `channel_key`; английская подпись из
   * базы остаётся запасным вариантом на случай нового канала, которого ещё нет
   * в словаре. Она же попадает в заголовок открывающегося списка, чтобы
   * заголовок и строка таблицы назывались одинаково.
   */
  const label = (r: Overview["leads"]["byChannel"][number]): string =>
    dbLabel(CHANNEL, r.channel_key, r.channel_name);

  /** Ячейка-ссылка. Без обработчика остаётся обычным числом. */
  const cell = (
    value: number,
    title: string,
    slice: Record<string, unknown>,
    extra?: React.ReactNode,
  ): React.ReactElement => {
    if (onDrill === undefined || value === 0) {
      return (
        <>
          {num(value)} {extra}
        </>
      );
    }
    return (
      <>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onDrill({ title, slice });
          }}
        >
          {num(value)}
        </a>{" "}
        {extra}
      </>
    );
  };

  return (
    <div className="scroll-x">
      <table className="data">
        <thead>
          <tr>
            <Th sort={S} col="source">{t(UI.source)}</Th>
            <Th sort={S} col="leads" num>{t(UI.conversations)}</Th>
            {/* Полоска-график: сортировать нечего — она рисует ту же величину,
                что стоит в колонке лидов. */}
            <th style={{ width: "22%" }} />
            <Th sort={S} col="qualified" num>{t(TX.colQualified)}</Th>
            <Th sort={S} col="deal" num>{t(TX.colBecameDeal)}</Th>
            <Th sort={S} col="completed" num>{t(TX.colCompletedEvents)}</Th>
          </tr>
        </thead>
        <tbody>
          {S.rows.slice(0, 10).map((r, i) => (
            <tr key={r.channel_key}>
              <td>
                <span className="dot" style={{ background: tint(i) }} />
                {label(r)}
                {r.basis_analytics_tag > 0 && (
                  <div className="drill-hint">
                    {num(r.basis_analytics_tag)} {t(TX.taggedByAnalytics)}
                  </div>
                )}
              </td>
              <td className="num">
                {cell(r.leads, `${label(r)} — ${t(TX.drillAllLeads)}`, {
                  sourceGroup: r.channel_key,
                })}
              </td>
              <td>
                <div className="funnel-bar" style={{ height: 8 }}>
                  <div
                    className="funnel-fill"
                    style={{
                      width: `${(100 * r.leads) / max}%`,
                      background: tint(i),
                    }}
                  />
                </div>
              </td>
              <td className="num">
                {cell(
                  r.qualified,
                  `${label(r)} — ${t(TX.drillQualified)}`,
                  { sourceGroup: r.channel_key, qualified: true },
                  <small style={{ color: "var(--ink-3)" }}>{pct(r.qualified, r.leads, 0)}</small>,
                )}
              </td>
              <td className="num">
                {cell(r.with_deal, `${label(r)} — ${t(TX.drillBecameDeal)}`, {
                  sourceGroup: r.channel_key,
                  hasDeal: true,
                })}
              </td>
              <td className="num">
                {cell(r.with_won_deal, `${label(r)} — ${t(TX.drillCompleted)}`, {
                  sourceGroup: r.channel_key,
                  won: true,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DailyChart({ daily }: { daily: Overview["leads"]["daily"] }): React.ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const tick = useThemeTick();
  /* Подписи серий переводим ДО эффекта: внутри `t` — это тема графика.
     Перерисовку после смены языка обеспечивает тот же `tick`, что и после
     смены темы: он следит и за атрибутом `data-lang`. */
  const nameLeads = t(UI.conversations);
  const nameQualified = t(TX.seriesQualified);
  useEffect(() => {
    if (!ref.current) return;
    /* Цвета читаем здесь, а не снаружи: значения нужны те, что стоят в теме на
       момент отрисовки. `tick` в зависимостях перерисовывает график после
       переключения темы — сам echarts о смене оформления не узнаёт. */
    const t = chartTheme();
    const chart = echarts.init(ref.current, undefined, { renderer: "canvas" });
    chart.setOption({
      grid: { left: 8, right: 8, top: 18, bottom: 4, containLabel: true },
      tooltip: { trigger: "axis", ...t.tooltip },
      xAxis: {
        type: "category",
        data: daily.map((d) => shortDate(d.d)),
        ...t.axis,
      },
      yAxis: {
        type: "value",
        ...t.axis,
        splitLine: t.splitLine,
      },
      series: [
        {
          /* Общий поток лидов — главная линия экрана, поэтому акцент темы.
             Прозрачность подклеиваем суффиксом: токены заданы шестизначным hex. */
          name: nameLeads,
          type: "line",
          smooth: true,
          symbol: "none",
          data: daily.map((d) => d.leads),
          lineStyle: { width: 2, color: t.accent },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: `${t.accent}55` },
              { offset: 1, color: `${t.accent}00` },
            ]),
          },
        },
        {
          /* Квалифицированные лиды — желаемый исход, отсюда цвет «хорошо». */
          name: nameQualified,
          type: "line",
          smooth: true,
          symbol: "none",
          data: daily.map((d) => d.qualified),
          lineStyle: { width: 2, color: t.pos },
        },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [daily, tick]);
  return <div ref={ref} style={{ height: 210 }} />;
}

export function RevenueSplit({
  tickets,
  food,
  banquet,
  other,
}: {
  tickets: number;
  food: number;
  banquet: number;
  other: number;
}): React.ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const tick = useThemeTick();
  const total = tickets + food + banquet + other;
  /* Подписи секторов — до эффекта: внутри `t` занято темой графика. */
  const nameTickets = t(TX.mixTickets);
  const nameFood = t(TX.mixFood);
  const nameBanquet = t(TX.mixBanquet);
  const nameOther = t(TX.mixOther);
  useEffect(() => {
    if (!ref.current || total === 0) return;
    /* Цвета — из темы во время отрисовки; `tick` в зависимостях пересобирает
       кольцо после переключения темы. */
    const t = chartTheme();
    const chart = echarts.init(ref.current);
    chart.setOption({
      tooltip: {
        trigger: "item",
        ...t.tooltip,
        /* Доля приходит от echarts уже посчитанной с двумя знаками — печатаем
           её тем же правилом, что и остальные проценты, но с её точностью. */
        formatter: (p: { name: string; value: number; percent: number }) =>
          `${p.name}<br/><b>${money(p.value, "AED")}</b> · ${pctValue(p.percent, 2)}`,
      },
      legend: {
        bottom: 0,
        textStyle: { color: t.ink2, fontSize: 11, fontFamily: t.font },
        icon: "circle",
      },
      series: [
        {
          type: "pie",
          radius: ["58%", "82%"],
          center: ["50%", "44%"],
          /* Разделитель секторов — цвет карточки под кольцом, а не серая линия:
             в светлой теме иначе получается тёмная обводка на белом. */
          itemStyle: { borderColor: t.card, borderWidth: 3 },
          label: { show: false },
          data: [
            /* Четыре статьи выручки равноправны — берём фирменный ряд по порядку:
               жёлтый, голубой, розовый, фиолетовый. */
            { name: nameTickets, value: tickets, itemStyle: { color: t.series[0] } },
            { name: nameFood, value: food, itemStyle: { color: t.series[1] } },
            { name: nameBanquet, value: banquet, itemStyle: { color: t.series[2] } },
            { name: nameOther, value: other, itemStyle: { color: t.series[3] } },
          ].filter((d) => d.value > 0),
        },
      ],
    });
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(ref.current);
    return () => {
      ro.disconnect();
      chart.dispose();
    };
  }, [tickets, food, banquet, other, total, tick]);

  if (total === 0) {
    return <div className="empty">{t(TX.noPos)}</div>;
  }
  return <div ref={ref} style={{ height: 250 }} />;
}

/**
 * Data freshness.
 *
 * Deliberately always on screen. A stale source that looks like a drop in sales
 * is the fastest way to lose trust in a dashboard, so the age of every source is
 * visible without asking.
 */
export function Freshness({ rows }: { rows: Overview["freshness"] }): React.ReactElement {
  /**
   * Идущий прогон — не упавший. Пока сюда попадал любой статус кроме `ok`,
   * запуск синхронизации сам по себе зажигал «3 sources failing», и красная
   * строка переставала что-либо значить.
   */
  const failed = rows.filter((r) => r.status !== "ok" && r.status !== "running");
  const busy = rows.filter((r) => r.status === "running").length;
  /**
   * Показываем САМЫЙ СТАРЫЙ источник, а не самый свежий.
   *
   * Раньше здесь стоял максимум `finished_at` по всем 18 источникам, и строка
   * «Data updated 14 s ago» соседствовала с источниками возраста три часа: одна
   * бодрая синхронизация закрывала собой все отстающие. Возраст данных на экране
   * определяется самым старым звеном, поэтому крупно печатается он, а самый
   * свежий уходит в подпись.
   */
  const stamps = rows
    .map((r) => r.finished_at)
    .filter(Boolean)
    .sort() as string[];
  const oldest = stamps.at(0);
  const newest = stamps.at(-1);
  return (
    <div className="freshness">
      <b>{t(UI.dataUpdated)}</b> {ago(oldest ?? null)}
      {newest !== undefined && oldest !== newest && (
        <div style={{ marginTop: 2, color: "var(--ink-3)", fontSize: 11 }}>
          {t(TX.oldestOf)} {num(stamps.length)}&nbsp;{plural(stamps.length, TX.sourcesOf)} ·{" "}
          {t(TX.newest)} {ago(newest)}
        </div>
      )}
      {failed.length > 0 && (
        <div className="err" style={{ marginTop: 4 }}>
          {num(failed.length)}&nbsp;{plural(failed.length, TX.sourcesFailing)}
        </div>
      )}
      {busy > 0 && (
        <div style={{ marginTop: 4, color: "var(--accent)" }}>
          {num(busy)}&nbsp;{plural(busy, TX.sourcesSyncing)}
        </div>
      )}
    </div>
  );
}

export function DeltaBadge({ value }: { value: number }): React.ReactElement {
  const tone = value > 0 ? "up" : value < 0 ? "down" : "flat";
  const sign = value > 0 ? "+" : "";
  return <span className={`delta ${tone}`}>{`${sign}${compact(value)}`}</span>;
}
